import React, { FormEvent, useRef, useState } from 'react';
import { Button, IconButton, InputAdornment, lighten, styled, TextField, Typography } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import * as yup from 'yup';
import Loading from 'components/loading/Loading';
import { api } from 'services/api';
import { toast } from 'react-toastify';
import history from 'services/history';

type ResetPasswordValidation = {
  email?: string;
  password?: string;
  password_confirmation?: string;
  token?: string;
};

const Container = styled('div')({
  display: 'flex',
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center',
});

const Content = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  width: 440,
  maxWidth: 'calc(100% - 40px)',
  padding: 30,
  margin: 20,
  position: 'relative',
  borderRadius: 16,
  backgroundColor: lighten(theme.palette.primary.main, 0.9),
  boxShadow: theme.shadows[5],
}));

const Header = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.75),
  textAlign: 'center',
}));

const Actions = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  alignItems: 'center',
  paddingTop: theme.spacing(1),
}));

const ResetPassword: React.FC = () => {
  const { token: routeToken } = useParams<{ token?: string }>();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validation, setValidation] = useState<ResetPasswordValidation>({});
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const confirmationRef = useRef<HTMLInputElement>(null);
  const token = routeToken || searchParams.get('token') || '';

  function focusFirstError(errors: ResetPasswordValidation) {
    if (errors.email) {
      emailRef.current?.focus();
      return;
    }

    if (errors.password) {
      passwordRef.current?.focus();
      return;
    }

    if (errors.password_confirmation) {
      confirmationRef.current?.focus();
    }
  }

  function submitReset() {
    setLoading(true);

    api
      .post('/password/reset', {
        email,
        password,
        password_confirmation: passwordConfirmation,
        token,
      })
      .then(() => {
        toast.success('Senha redefinida com sucesso');
        history.push('/login');
      })
      .catch(err => {
        if (err.response?.data?.errors) {
          const errors = Object.keys(err.response.data.errors).reduce((acc, key) => {
            acc[key as keyof ResetPasswordValidation] = err.response.data.errors[key][0];
            return acc;
          }, {} as ResetPasswordValidation);

          setValidation(errors);
          focusFirstError(errors);
          return;
        }

        if (err.response?.data?.error) {
          toast.error(err.response.data.error);
          return;
        }

        toast.error('Nao foi possivel redefinir a senha');
      })
      .finally(() => setLoading(false));
  }

  function handleSubmit(e?: FormEvent<HTMLFormElement>) {
    e?.preventDefault();
    setValidation({});

    const schema = yup.object().shape({
      token: yup.string().required('Token de redefinição inválido'),
      email: yup.string().email('Informe um e-mail válido').required('Informe seu e-mail'),
      password: yup.string().min(6, 'A senha deve ter pelo menos 6 caracteres').required('Informe a nova senha'),
      password_confirmation: yup
        .string()
        .oneOf([yup.ref('password')], 'As senhas não conferem')
        .required('Confirme a nova senha'),
    });

    schema
      .validate({ email, password, password_confirmation: passwordConfirmation, token }, { abortEarly: false })
      .then(submitReset)
      .catch((err: yup.ValidationError) => {
        const errors = err.inner.reduce((acc, item) => {
          if (item.path && !acc[item.path as keyof ResetPasswordValidation]) {
            acc[item.path as keyof ResetPasswordValidation] = item.message;
          }
          return acc;
        }, {} as ResetPasswordValidation);

        setValidation(errors);
        focusFirstError(errors);

        if (errors.token) toast.error(errors.token);
      });
  }

  return (
    <Container>
      <Content>
        {loading && <Loading />}
        <Header>
          <Typography variant="h5">Criar nova senha</Typography>
          <Typography variant="body2" color="textSecondary">
            Informe seu e-mail e escolha uma nova senha de acesso.
          </Typography>
        </Header>

        <form onSubmit={handleSubmit}>
          <TextField
            inputRef={emailRef}
            label="E-mail"
            placeholder="Digite seu e-mail"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            error={!!validation.email}
            helperText={validation.email}
            autoComplete="email"
            fullWidth
            margin="normal"
          />

          <TextField
            inputRef={passwordRef}
            label="Nova senha"
            placeholder="Digite a nova senha"
            value={password}
            onChange={e => setPassword(e.target.value)}
            error={!!validation.password}
            helperText={validation.password}
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            fullWidth
            margin="normal"
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPassword(value => !value)}>
                      {showPassword ? <VisibilityOff color="primary" /> : <Visibility color="primary" />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />

          <TextField
            inputRef={confirmationRef}
            label="Confirmar senha"
            placeholder="Confirme a nova senha"
            value={passwordConfirmation}
            onChange={e => setPasswordConfirmation(e.target.value)}
            error={!!validation.password_confirmation}
            helperText={validation.password_confirmation}
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            fullWidth
            margin="normal"
          />
          <button type="submit" style={{ display: 'none' }} />
        </form>

        <Actions>
          <Button fullWidth variant="contained" disabled={loading} onClick={() => handleSubmit()}>
            Redefinir senha
          </Button>
          <Link to="/login">Voltar para o login</Link>
        </Actions>
      </Content>
    </Container>
  );
};

export default ResetPassword;
